import React, { useEffect, useMemo, useState } from "react";
import AdminModal from "components/admin/AdminModal";
import { NO_OG_GROUP_DESCRIPTION, NO_OG_GROUP_LABEL } from "./constants";
import { OgUserGroup } from "./type";

interface MoveToModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Every OG user group, as GetOgUserGroups returned them to the page. */
  groups: OgUserGroup[];
  /**
   * The group the selected users are already in — never offered as a target.
   * null on the Users tab, which is the "General Users" list itself.
   */
  currentRoleId: number | null;
  /** null removes the users from every OG group. */
  onMove: (roleId: number | null) => void;
  /** Keeps the button disabled while the move calls are in flight. */
  moving?: boolean;
}

interface MoveOption {
  /** null = the "General Users" option, which clears the OG membership. */
  roleId: number | null;
  label: string;
  description: string;
}

/**
 * Target picker for the "Move to" action, shared by every tab. The options are
 * the groups the page already loaded from GetOgUserGroups, so any role the
 * admin adds through User Role Configuration shows up here without a code
 * change; "General Users" is the FE-side name for sending roleId: null.
 */
const MoveToModal: React.FC<MoveToModalProps> = ({
  isOpen,
  onClose,
  groups,
  currentRoleId,
  onMove,
  moving = false,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  const options = useMemo<MoveOption[]>(() => {
    const groupOptions: MoveOption[] = groups
      .filter((group) => group.id !== currentRoleId)
      .map((group) => ({
        roleId: group.id,
        label: group.roleName,
        description: group.description ?? "",
      }));

    // The Users tab *is* the General Users list, so offering it there as a
    // destination would be a move to where you already are. The group tabs
    // need it as their way out of a group.
    return currentRoleId === null
      ? groupOptions
      : [
          ...groupOptions,
          {
            roleId: null,
            label: NO_OG_GROUP_LABEL,
            description: NO_OG_GROUP_DESCRIPTION,
          },
        ];
  }, [groups, currentRoleId]);

  // Reset the pick each time the modal opens, so a target chosen for the last
  // selection is not still armed for the next one.
  useEffect(() => {
    if (isOpen) setSelectedRoleId(options[0]?.roleId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * What Move actually sends. `selectedRoleId` is only trusted while it names
   * an option that is still on screen: opening before the groups have loaded
   * (or after a role is deleted) leaves it null, and null is not "nothing
   * picked" — it is the General Users write, which on the Users tab is not even
   * offered. Falling back to the first option keeps the button honest.
   */
  const targetRoleId = options.some((option) => option.roleId === selectedRoleId)
    ? selectedRoleId
    : (options[0]?.roleId ?? null);

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={() => {
        if (!moving) onClose();
      }}
      title="Move To"
      size="lg"
      showCloseButton={!moving}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={moving}
            className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onMove(targetRoleId)}
            disabled={moving || options.length === 0}
            className="px-6 py-2 text-sm bg-[#FFBC00] text-white font-medium rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {moving ? "Moving..." : "Move"}
          </button>
        </>
      }
    >
      <p className="text-sm text-lightgray-200 -mt-2 mb-4">
        Select the user group you want to move this user to.
      </p>

      {options.length === 0 ? (
        <p className="text-sm text-lightgray-200 py-4 text-center">
          No user groups available.
        </p>
      ) : (
        <div className="space-y-3">
          {options.map((option) => {
            const isSelected = targetRoleId === option.roleId;
            return (
              <label
                key={option.roleId ?? "none"}
                className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors duration-150 ${
                  isSelected
                    ? "bg-violet-50 border-violet-100"
                    : "bg-white border-transparent hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="move-to-target"
                  className="mt-0.5 accent-violet-100 cursor-pointer"
                  checked={isSelected}
                  disabled={moving}
                  onChange={() => setSelectedRoleId(option.roleId)}
                />
                <span>
                  <span className="block text-grease-100">{option.label}</span>
                  {option.description && (
                    <span className="block text-xs text-lightgray-200 mt-1 break-words">
                      {option.description}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </AdminModal>
  );
};

export default MoveToModal;
